import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, User, Lock, Eye, EyeOff, AlertCircle, Calendar, Users, FileText, Activity, Shield, Mail, KeyRound } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(true);
  
  const { login, verify2FA, user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    // Redirect based on role
    if (user.role === 'inventory_manager') {
      return <Navigate to="/mobile-inventory" replace />;
    }
    const redirectPath = user.role === 'doctor' ? '/visits' : '/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(email, password);
      
      // Check if 2FA is required
      if (result?.requires_2fa) {
        setRequires2FA(true);
        setError('');
      } else if (result?.user) {
        // Login successful, redirect based on role
        let redirectPath = '/dashboard';
        if (result.user?.role === 'inventory_manager') {
          redirectPath = '/mobile-inventory';
        } else if (result.user?.role === 'doctor') {
          redirectPath = '/visits';
        }
        navigate(redirectPath);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Invalid credentials';
      if (errorMessage === 'Invalid credentials') {
        setError('Incorrect username or password. Please try again.');
      } else if (errorMessage === 'Account is disabled') {
        setError('Your account has been disabled. Please contact support.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const userData = await verify2FA(email, verificationCode, trustDevice);
      // Redirect based on role
      let redirectPath = '/dashboard';
      if (userData?.role === 'inventory_manager') {
        redirectPath = '/mobile-inventory';
      } else if (userData?.role === 'doctor') {
        redirectPath = '/visits';
      }
      navigate(redirectPath);
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setRequires2FA(false);
    setVerificationCode('');
    setError('');
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Side - Login Form */}
      <div className="w-full lg:w-[45%] flex flex-col justify-center px-8 lg:px-16 py-12 bg-white">
        <div className="w-full max-w-md mx-auto">
          {/* Logo and Brand */}
          <div className="mb-12 flex flex-col items-center text-center">
            <img 
              src="/images/tu_clinicflow.png" 
              alt="TU Clinic Flow" 
              className="w-full max-w-[300px] h-auto object-contain drop-shadow-[0_15px_25px_rgba(37,99,235,0.35)]"
            />
          </div>

          {/* Login Header */}
          <div className="mb-8">
            {requires2FA ? (
              <>
                <h2 className="text-2xl font-semibold text-slate-900">Verify Your Identity</h2>
                <p className="text-slate-500 mt-1">We sent a code to <strong>{email}</strong></p>
              </>
            ) : null}
          </div>

          {/* 2FA Verification Form */}
          {requires2FA ? (
            <form onSubmit={handleVerify2FA} className="space-y-5">
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={verificationCode}
                  onChange={(e) => { setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                  required
                  maxLength={6}
                  className="pl-11 h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 focus:ring-blue-500/20 rounded-lg text-slate-700 text-center text-xl tracking-widest font-mono"
                  data-testid="2fa-code-input"
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex items-center">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="trustDevice" 
                    checked={trustDevice}
                    onCheckedChange={setTrustDevice}
                    className="border-slate-300 data-[state=checked]:bg-blue-600"
                  />
                  <label htmlFor="trustDevice" className="text-sm text-slate-600 cursor-pointer">
                    Trust this device
                  </label>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold text-base rounded-xl shadow-lg shadow-blue-600/25 transition-all duration-200 active:scale-[0.99]" 
                disabled={loading || verificationCode.length !== 6}
                data-testid="verify-2fa-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Continue'
                )}
              </Button>

              <button
                type="button"
                onClick={handleBackToLogin}
                className="w-full text-sm text-slate-500 hover:text-slate-700"
              >
                ← Back to login
              </button>
            </form>
          ) : (
            /* Login Form */
            <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600 ml-1">Email</label>
              <div className="relative group">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-blue-600 group-focus-within:bg-blue-600 group-focus-within:text-white transition-colors">
                  <Mail className="w-4 h-4" />
                </span>
                <Input
                  type="email"
                  placeholder="you@clinic.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  required
                  className="pl-12 h-11 bg-slate-50/80 border border-slate-200 shadow-sm hover:border-slate-300 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl text-slate-800 text-base font-medium transition-all duration-200"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600 ml-1">Password</label>
              <div className="relative group">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-blue-600 group-focus-within:bg-blue-600 group-focus-within:text-white transition-colors">
                  <KeyRound className="w-4 h-4" />
                </span>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  required
                  className="pl-12 pr-11 h-11 bg-slate-50/80 border border-slate-200 shadow-sm hover:border-slate-300 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl text-slate-800 text-base font-medium transition-all duration-200"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex items-center">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="remember" 
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  className="border-slate-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                />
                <label htmlFor="remember" className="text-sm text-slate-600 cursor-pointer">
                  Remember me
                </label>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-11 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold text-base rounded-xl shadow-lg shadow-blue-600/25 hover:shadow-blue-700/30 transition-all duration-200 active:scale-[0.99]" 
              disabled={loading}
              data-testid="login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Signing in...
                </>
              ) : (
                'Log in'
              )}
            </Button>
          </form>
          )}

          {/* Footer */}
          <div className="mt-12 text-center text-xs text-slate-400">
            <p>© 2026 ClinicFlow. All rights reserved.</p>
            <div className="mt-2 space-x-3">
              <a href="#" className="hover:text-slate-600">Privacy Policy</a>
              <span>|</span>
              <a href="#" className="hover:text-slate-600">Terms of Use</a>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Visual Panel (Original Dark Slate/Blue Theme) */}
      <div className="hidden lg:flex lg:w-[55%] bg-gradient-to-br from-slate-800 via-slate-900 to-blue-900 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.3) 0%, transparent 50%),
                             radial-gradient(circle at 75% 75%, rgba(59, 130, 246, 0.2) 0%, transparent 50%)`
          }}></div>
        </div>

        {/* Top-left darker blue gradient accent starting from the corner */}
        <div className="absolute -top-10 -left-10 w-80 h-80 bg-[radial-gradient(circle_at_top_left,rgba(29,78,216,0.9),rgba(30,58,138,0.55)_35%,transparent_70%)] blur-2xl pointer-events-none" aria-hidden="true"></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-16">
          {/* Main Illustration Area */}
          <div className="relative w-full max-w-lg">
            {/* Central Card */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 shadow-2xl">
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Activity className="w-8 h-8 text-white" />
                </div>
              </div>
              <h2 className="text-2xl font-semibold text-white text-center mb-2">
                Complete Healthcare Solution
              </h2>
              <p className="text-slate-400 text-center text-sm">
                Streamline your clinic operations with our integrated management platform
              </p>
            </div>

            {/* Floating Feature Cards */}
            <div className="absolute -top-8 -left-8 bg-slate-800/80 backdrop-blur border border-slate-700/50 rounded-xl p-4 shadow-xl animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Patient Management</p>
                  <p className="text-slate-400 text-xs">Track all records</p>
                </div>
              </div>
            </div>

            <div className="absolute -top-4 -right-12 bg-slate-800/80 backdrop-blur border border-slate-700/50 rounded-xl p-4 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Appointments</p>
                  <p className="text-slate-400 text-xs">Easy scheduling</p>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-6 -left-12 bg-slate-800/80 backdrop-blur border border-slate-700/50 rounded-xl p-4 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Billing & Reports</p>
                  <p className="text-slate-400 text-xs">Complete insights</p>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-10 -right-8 bg-slate-800/80 backdrop-blur border border-slate-700/50 rounded-xl p-4 shadow-xl animate-pulse" style={{animationDelay: '1s'}}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Secure & HIPAA</p>
                  <p className="text-slate-400 text-xs">Data protection</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Stats */}
          <div className="mt-16 grid grid-cols-3 gap-8 text-center">
            <div>
              <p className="text-3xl font-bold text-white">5+</p>
              <p className="text-slate-400 text-sm">Locations</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">10k+</p>
              <p className="text-slate-400 text-sm">Patients</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">24/7</p>
              <p className="text-slate-400 text-sm">Support</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
